import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { reportsService } from "@/services";
import type { DashboardData } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { roleLabel } from "@/config/roles";
import { StatCard } from "@/components/base/StatCard";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { ErrorState } from "@/components/base/ErrorState";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatINR } from "@/utils/format";
import { TrendChart } from "./components/TrendChart";
import { GroupPerformanceChart } from "./components/GroupPerformanceChart";
import { AGEING_META, AGEING_ORDER } from "@/pages/reports/components/labels";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await reportsService.getDashboardData());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load dashboard data.");
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold text-foreground-950">Dashboard</h1>
          <p className="mt-0.5 text-sm text-foreground-500">
            Welcome back, <span className="font-medium text-foreground-800">{user?.name}</span> — working as {roleLabel(role)}.
          </p>
        </div>
        {data ? (
          <p className="text-xs text-foreground-500">
            Collections this month: <span className="tnum font-semibold text-foreground-800">{formatINR(data.monthCollection)}</span>
          </p>
        ) : null}
      </div>

      {error ? <div className="mb-3"><ErrorState message={error} onRetry={load} /></div> : null}

      {!data ? (
        <div className="h-72 animate-pulse rounded-lg bg-background-200" />
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard label="Active Groups" value={data.activeGroups} icon="ri-archive-line" iconClass="bg-primary-100 text-primary-700" onClick={() => navigate("/groups")} />
            <StatCard label="Total Members" value={data.totalMembers} icon="ri-group-line" iconClass="bg-accent-100 text-accent-700" onClick={() => navigate("/members")} />
            <StatCard label="Collections This Month" value={formatINR(data.monthCollection)} icon="ri-wallet-3-line" iconClass="bg-success-100 text-success-700" tone="success" onClick={() => navigate("/reports/collection-register")} />
            <StatCard label="Pending / Overdue" value={formatINR(data.pendingOverdue)} icon="ri-time-line" iconClass="bg-warning-100 text-warning-700" tone="warning" onClick={() => navigate("/reports/outstanding")} />
          </div>

          {/* Ageing breakdown */}
          <Card
            className="mt-4"
            title="Outstanding Ageing"
            subtitle="Where the money owed is concentrated"
            actions={
              <Button size="sm" variant="ghost" onClick={() => navigate("/reports/outstanding")}>
                View report
              </Button>
            }
          >
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
              {AGEING_ORDER.map((b) => {
                const item = data.ageing.find((a) => a.bucket === b);
                const amount = item?.amount ?? 0;
                const total = data.ageing.reduce((s, a) => s + a.amount, 0);
                const pct = total > 0 ? Math.round((amount / total) * 100) : 0;
                const meta = AGEING_META[b];
                return (
                  <div
                    key={b}
                    onClick={() => navigate("/reports/outstanding")}
                    className="group cursor-pointer rounded-lg border-2 border-background-300 bg-background-100/40 p-3 transition-all hover:border-primary-400 hover:bg-background-100 hover:shadow-sm"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-foreground-500 group-hover:text-foreground-800">{meta.label}</p>
                      <Badge color={meta.badge}>{pct}%</Badge>
                    </div>
                    <p className={`tnum mt-2 text-lg font-semibold ${meta.tone === "danger" ? "text-danger-600" : meta.tone === "warning" ? "text-warning-600" : "text-foreground-950"}`}>
                      {formatINR(amount)}
                    </p>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-background-200">
                      <div className="h-full rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Charts */}
          <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-2">
            <Card
              title="Collections vs Payouts"
              subtitle="Last 12 months"
              actions={
                <Button size="sm" variant="ghost" onClick={() => navigate("/reports/ledger")}>
                  Ledger
                </Button>
              }
            >
              <TrendChart data={data.trend} />
            </Card>
            <Card
              title="Group Performance"
              subtitle="Collected vs expected, current cycle"
              actions={
                <Button size="sm" variant="ghost" onClick={() => navigate("/reports/group-performance")}>
                  Report
                </Button>
              }
            >
              {data.groupPerformance.length ? (
                <GroupPerformanceChart data={data.groupPerformance} />
              ) : (
                <div className="flex h-[260px] items-center justify-center text-sm text-foreground-400">No active collection data yet.</div>
              )}
            </Card>
          </div>

          {/* Lists */}
          <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
            <Card
              title="Upcoming Auctions"
              subtitle="Next to be held"
              className="lg:col-span-1"
              actions={
                <Button size="sm" variant="ghost" onClick={() => navigate("/auction")}>
                  View all
                </Button>
              }
            >
              <div className="flex flex-col divide-y divide-background-100">
                {data.upcomingAuctions.length === 0 ? (
                  <p className="py-6 text-center text-sm text-foreground-400">No auctions scheduled.</p>
                ) : (
                  data.upcomingAuctions.map((a) => (
                    <button key={a.id} type="button" onClick={() => navigate(`/auction/${a.groupId}/${a.cycle}`)} className="flex cursor-pointer items-center justify-between gap-2 py-2 text-left hover:bg-background-100/50 transition-colors">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground-900">{a.groupName}</p>
                        <p className="text-xs text-foreground-500">Cycle {a.cycle} · {a.periodLabel}</p>
                      </div>
                      <div className="flex flex-none items-center gap-2">
                        <span className="tnum text-xs text-foreground-500">{new Date(a.scheduledDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
                        <i className="ri-arrow-right-line text-sm text-foreground-300" />
                      </div>
                    </button>
                  ))
                )}
              </div>
            </Card>

            <Card
              title="Recent Collections"
              subtitle="Latest receipts"
              className="lg:col-span-2"
              actions={
                <Button size="sm" variant="ghost" onClick={() => navigate("/collection/day-book")}>
                  Day book
                </Button>
              }
            >
              <div className="flex flex-col divide-y divide-background-100">
                {data.recentCollections.length === 0 ? (
                  <p className="py-6 text-center text-sm text-foreground-400">No collections recorded.</p>
                ) : (
                  data.recentCollections.map((r) => (
                    <div
                      key={r.id}
                      onClick={() => navigate(`/collection/receipts/${r.id}`)}
                      className="flex cursor-pointer items-center justify-between gap-2 py-2 px-1 rounded-md transition-colors hover:bg-background-100/80"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="tnum text-xs text-foreground-500">{new Date(r.receivedOn).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground-900">{r.memberName}</p>
                          <p className="truncate text-xs text-foreground-500">{r.lines.map((l) => l.groupName).join(", ")}</p>
                        </div>
                      </div>
                      <div className="flex flex-none items-center gap-2">
                        <span className="tnum text-xs text-foreground-400">{r.receiptNumber}</span>
                        <span className="tnum text-sm font-semibold text-foreground-900">{formatINR(r.total)}</span>
                        {r.reversedAt ? <Badge color="danger">Reversed</Badge> : null}
                        <i className="ri-arrow-right-s-line text-base text-foreground-400" />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}